import { Alert, Button, Descriptions, Input, Modal, Space, Typography, Upload, message } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useClearFirebaseSettings,
  useTestFirebaseSettings,
  useUploadFirebaseSettings,
} from './Hooks';
import { unixToLocalDate } from '../../utils/CommonFunctions';

const { Text, Paragraph } = Typography;

const formatUploadedAt = (value) => {
  if (value == null || value === '') return '';
  const asNumber = Number(value);
  if (Number.isFinite(asNumber) && asNumber > 1_000_000_000) {
    return unixToLocalDate(asNumber);
  }
  return String(value);
};

const FirebaseSettingsTab = ({ firebaseMeta = {} }) => {
  const { t } = useTranslation(['settings', 'common']);
  const [testToken, setTestToken] = useState('');
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [localMeta, setLocalMeta] = useState(null);

  const displayMeta = localMeta || firebaseMeta || {};

  const uploadMutation = useUploadFirebaseSettings({
    onSuccess: (data) => {
      setLocalMeta({
        configured: true,
        source: data?.source || 'settings',
        projectId: data?.projectId,
        clientEmail: data?.clientEmail,
        uploadedAt: data?.uploadedAt,
      });
      message.success(t('firebaseUploadSuccess'));
    },
    onError: (error) => message.error(error?.message || t('common:fetchError')),
  });

  const testMutation = useTestFirebaseSettings({
    onSuccess: (data) => {
      message.success(
        `${t('firebaseTestSuccess')} (${data?.source || 'settings'}: ${data?.projectId || ''})`,
      );
      setTestModalOpen(false);
      setTestToken('');
    },
    onError: (error) => message.error(error?.message || t('firebaseTestFailed')),
  });

  const clearMutation = useClearFirebaseSettings({
    onSuccess: () => {
      setLocalMeta({ configured: false, source: 'env' });
      message.success(t('firebaseClearSuccess'));
    },
    onError: (error) => message.error(error?.message || t('common:fetchError')),
  });

  const handleJsonUpload = (file) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const parsed = JSON.parse(event.target?.result || '{}');
        if (
          parsed.type !== 'service_account' ||
          !parsed.project_id ||
          !parsed.private_key ||
          !parsed.client_email
        ) {
          message.error(t('firebaseInvalidJson'));
          return;
        }
        if (!String(parsed.private_key).includes('PRIVATE KEY')) {
          message.error(t('firebaseInvalidJson'));
          return;
        }
        await uploadMutation.mutateAsync(parsed);
      } catch {
        message.error(t('firebaseInvalidJson'));
      }
    };
    reader.readAsText(file);
  };

  const confirmClear = () => {
    Modal.confirm({
      title: t('firebaseClearConfirmTitle'),
      content: t('firebaseClearConfirmBody'),
      okText: t('firebaseClearButton'),
      okType: 'danger',
      onOk: () => clearMutation.mutateAsync(),
    });
  };

  return (
    <div className="max-h-[74vh] overflow-y-scroll pr-4 mt-4">
      <TitleSection title={t('firebaseTabTitle')} description={t('firebaseTabDescription')} />

      <Descriptions bordered column={1} size="small" className="mb-4">
        <Descriptions.Item label={t('firebaseStatusLabel')}>
          {displayMeta?.configured ? (
            <Text type="success">{t('firebaseConfigured')} (DB)</Text>
          ) : (
            <Text type="warning">{t('firebaseNotConfigured')}</Text>
          )}
        </Descriptions.Item>
        {displayMeta?.source ? (
          <Descriptions.Item label={t('firebaseSourceLabel')}>
            {displayMeta.source === 'settings'
              ? 'Database (CMS upload)'
              : 'Environment fallback'}
          </Descriptions.Item>
        ) : null}
        {displayMeta?.projectId ? (
          <Descriptions.Item label={t('firebaseProjectIdLabel')}>
            {displayMeta.projectId}
          </Descriptions.Item>
        ) : null}
        {displayMeta?.clientEmail ? (
          <Descriptions.Item label={t('firebaseClientEmailLabel')}>
            <Text copyable className="break-all">
              {displayMeta.clientEmail}
            </Text>
          </Descriptions.Item>
        ) : null}
        {displayMeta?.uploadedAt ? (
          <Descriptions.Item label={t('firebaseUploadedAtLabel')}>
            {formatUploadedAt(displayMeta.uploadedAt)}
          </Descriptions.Item>
        ) : null}
      </Descriptions>

      {!displayMeta?.configured ? (
        <Alert type="info" showIcon className="mb-4" message={t('firebaseEnvFallbackHint')} />
      ) : (
        <Alert type="success" showIcon className="mb-4" message={t('firebaseDbActiveHint')} />
      )}

      <Space wrap className="mb-4">
        <Upload
          accept=".json,application/json"
          showUploadList={false}
          beforeUpload={(file) => {
            handleJsonUpload(file);
            return false;
          }}
        >
          <Button type="primary" loading={uploadMutation.isPending}>
            {t('firebaseUploadButton')}
          </Button>
        </Upload>
        <Button onClick={() => setTestModalOpen(true)} disabled={!displayMeta?.configured}>
          {t('firebaseTestButton')}
        </Button>
        <Button danger onClick={confirmClear} loading={clearMutation.isPending}>
          {t('firebaseClearButton')}
        </Button>
      </Space>

      <Paragraph type="secondary">{t('firebaseSecurityNote')}</Paragraph>
      <Paragraph type="secondary">{t('firebaseProjectMatchNote')}</Paragraph>

      <Modal
        title={t('firebaseTestModalTitle')}
        open={testModalOpen}
        onCancel={() => setTestModalOpen(false)}
        onOk={() => testMutation.mutateAsync({ testToken })}
        confirmLoading={testMutation.isPending}
        okButtonProps={{ disabled: !testToken.trim() }}
      >
        <Input
          placeholder={t('firebaseTestTokenPlaceholder')}
          value={testToken}
          onChange={(e) => setTestToken(e.target.value)}
        />
      </Modal>
    </div>
  );
};

function TitleSection({ title, description }) {
  return (
    <div className="mb-4">
      <Typography.Title level={5}>{title}</Typography.Title>
      <Text type="secondary">{description}</Text>
    </div>
  );
}

export default FirebaseSettingsTab;
